import { useComputeReturnFees } from "../queries/use-compute-return-fees";
import { usePublicOrder } from "../queries/use-public-order";

export const PRICE_PLACEHOLDER = "…";

type Resolution = "refund" | "discount" | "exchange" | "exchangeCredit";

export type HypotheticalReturnFeeState =
  /** Query pending — nothing to display yet. */
  | { status: "loading"; fee: undefined; fallbackFee: number }
  /** Shop opted out (e.g. discount with `applyReturnShippingFeesForDiscountRule` disabled). Fee is hard zero. */
  | { status: "opt-out"; fee: 0; fallbackFee: number }
  /** Dynamic fee returned by the backend. `isStale` true while refetching. */
  | { status: "ready"; fee: number; fallbackFee: number; isStale: boolean };

/**
 * Resolves a per-resolution return fee from the `compute-return-fees` query
 * with three distinct states:
 *
 * - `loading`: no data yet — show PRICE_PLACEHOLDER, disable card.
 * - `opt-out`: backend returned `null` for this resolution — fee is hard 0.
 * - `ready`: `fee` is the dynamic amount. `isStale` true while refetching.
 *
 * `fallbackFee` exposes the legacy static amount (from order.financialInformations)
 * so callers can avoid flicker on first mount by opting in explicitly.
 */
export function useHypotheticalReturnFee(
  resolution: Resolution,
  {
    orderId,
    shopId,
    pendingLineItemId,
    pendingReasonId,
  }: {
    orderId: string;
    shopId: string;
    pendingLineItemId?: string;
    pendingReasonId?: string;
  },
): HypotheticalReturnFeeState {
  const { data: order } = usePublicOrder(orderId);
  const { data: returnFees, isFetching, isPlaceholderData, isError } =
    useComputeReturnFees({ orderId, shopId, pendingLineItemId, pendingReasonId });

  const fallbackFee =
    (order as any)?.financialInformations?.return_shipping_paid?.presentment_amount ?? 0;

  // Query failed with no prior data — fall back to the static order fee.
  if (isError && !returnFees) {
    return { status: "ready", fee: fallbackFee, fallbackFee, isStale: false };
  }

  if (!returnFees) return { status: "loading", fee: undefined, fallbackFee };

  const dynamicFee = returnFees.byResolution[resolution];
  if (dynamicFee === null) return { status: "opt-out", fee: 0, fallbackFee };

  return {
    status: "ready",
    fee: dynamicFee,
    fallbackFee,
    isStale: isFetching && isPlaceholderData,
  };
}
